import React from 'react';
import { localePath } from 'utils/localePath';

const UnifiedFooter = ({ translations }) => {
  // 調試：檢查 translations
  console.log('UnifiedFooter translations:', translations);

  // 安全獲取翻譯，提供 fallback
  const footer = translations?.footer || {};
  const siteName = translations?.site_name || 'TutorNetworking';
  const supportEmail = translations?.common?.support_email || 'support@tutornetworking.com';

  return (
    <footer className="bg-gray-900 text-white mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* About Section */}
          <div>
            <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-[#6699FF] to-[#3366FF] bg-clip-text text-transparent">
              {siteName}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              {footer.description || 'A lightweight SaaS platform for small teaching brands.'}
            </p>
          </div>

          {/* Links Section */}
          <div>
            <h4 className="text-lg font-semibold mb-4">
              {footer.links_title || 'Quick Links'}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href={localePath("/about")}
                  className="text-gray-400 hover:text-[#6699FF] transition-colors duration-200"
                >
                  {footer.about_us || 'About Us'}
                </a>
              </li>
              <li>
                <a
                  href={localePath("/faq")}
                  className="text-gray-400 hover:text-[#6699FF] transition-colors duration-200"
                >
                  {footer.faq || 'FAQ'}
                </a>
              </li>
              <li>
                <a
                  href={localePath("/terms")}
                  className="text-gray-400 hover:text-[#6699FF] transition-colors duration-200"
                >
                  {footer.terms || 'Terms of Service'}
                </a>
              </li>
              <li>
                <a
                  href={localePath("/privacy")}
                  className="text-gray-400 hover:text-[#6699FF] transition-colors duration-200"
                >
                  {footer.privacy || 'Privacy Policy'}
                </a>
              </li>
              <li>
                <a
                  href={localePath("/for-teachers")}
                  className="text-gray-400 hover:text-[#6699FF] transition-colors duration-200"
                >
                  {footer.for_teachers || 'For Teachers'}
                </a>
              </li>
              <li>
                <a
                  href={localePath("/tools")}
                  className="text-gray-400 hover:text-[#6699FF] transition-colors duration-200"
                >
                  {footer.tools || 'Free Tools'}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Section */}
          <div>
            <h4 className="text-lg font-semibold mb-4">
              {footer.contact_title || 'Contact Us'}
            </h4>
            <div className="text-sm space-y-3">
              <div>
                <p className="text-gray-400 mb-2">
                  {footer.email_label || 'Email:'}
                </p>
                <a
                  href={`mailto:${supportEmail}`}
                  className="text-[#6699FF] hover:text-[#3366FF] transition-colors duration-200 font-medium"
                  aria-label={`Send email to ${supportEmail}`}
                >
                  {supportEmail}
                </a>
              </div>
              <div>
                <p className="text-gray-400 mb-2">
                  {footer.line_label || 'Customer Service:'}
                </p>
                <a
                  href="https://line.me/R/ti/p/@615llvhr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#6699FF] hover:text-[#3366FF] transition-colors duration-200 font-medium inline-flex items-center gap-1"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                  </svg>
                  {footer.line_text || 'Official Line'}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 pt-8 text-center">
          <p className="text-gray-400 text-sm">
            {footer.copyright?.replace('%{year}', new Date().getFullYear()) || `© ${new Date().getFullYear()} TutorNetworking. All rights reserved.`}
          </p>
          <p className="text-gray-500 text-xs mt-2">
            {footer.company_info || '多麗亞資訊有限公司｜統一編號：60771553'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default UnifiedFooter;
